import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type {
  LoteArbitralResumo,
  LoteArbitralRow,
  LoteArbitralState,
  LoteArbitralStep,
  LoteArbitralTotais,
} from '../types/loteArbitralTypes';

const emptyTotais: LoteArbitralTotais = { total: 0, validos: 0, invalidos: 0 };

const initialState: LoteArbitralState = {
  currentStep: 'upload',
  nomeLote: '',
  fileName: null,
  rows: [],
  totais: emptyTotais,
  isProcessing: false,
  lotes: [],
  ultimoLoteCriado: null,
};

const computeTotais = (rows: LoteArbitralRow[]): LoteArbitralTotais => ({
  total: rows.length,
  validos: rows.filter((row) => row.status === 'valid').length,
  invalidos: rows.filter((row) => row.status === 'invalid').length,
});

const loteArbitralSlice = createSlice({
  name: 'arbitralLote',
  initialState,
  reducers: {
    setLoteStep: (state, action: PayloadAction<LoteArbitralStep>) => {
      state.currentStep = action.payload;
    },
    setLoteNome: (state, action: PayloadAction<string>) => {
      state.nomeLote = action.payload;
    },
    setLoteRows: (state, action: PayloadAction<{ rows: LoteArbitralRow[]; fileName: string }>) => {
      state.rows = action.payload.rows;
      state.fileName = action.payload.fileName;
      state.totais = computeTotais(action.payload.rows);
    },
    removeLoteRow: (state, action: PayloadAction<string>) => {
      state.rows = state.rows.filter((row) => row.id !== action.payload);
      state.totais = computeTotais(state.rows);
    },
    setLoteProcessing: (state, action: PayloadAction<boolean>) => {
      state.isProcessing = action.payload;
    },
    hydrateLotesArbitrais: (state, action: PayloadAction<LoteArbitralResumo[]>) => {
      state.lotes = action.payload;
    },
    registrarLoteArbitral: (state, action: PayloadAction<LoteArbitralResumo>) => {
      state.lotes = [action.payload, ...state.lotes];
    },
    finalizarLoteCriado: (state, action: PayloadAction<LoteArbitralResumo>) => {
      state.lotes = [action.payload, ...state.lotes];
      state.ultimoLoteCriado = action.payload;
      state.currentStep = 'sucesso';
      state.nomeLote = '';
      state.fileName = null;
      state.rows = [];
      state.totais = emptyTotais;
      state.isProcessing = false;
    },
    resetLoteArbitral: (state) => {
      state.currentStep = 'upload';
      state.nomeLote = '';
      state.fileName = null;
      state.rows = [];
      state.totais = emptyTotais;
      state.isProcessing = false;
      state.ultimoLoteCriado = null;
    },
  },
});

export const {
  setLoteStep,
  setLoteNome,
  setLoteRows,
  removeLoteRow,
  setLoteProcessing,
  hydrateLotesArbitrais,
  registrarLoteArbitral,
  finalizarLoteCriado,
  resetLoteArbitral,
} = loteArbitralSlice.actions;

export default loteArbitralSlice.reducer;
